import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { getHudDockTop } from '../utils/hudDockGeometry';
import './RollLogTab.scss';

const sameAnchor = (a, b) => a && b && a.left === b.left && a.bottom === b.bottom && a.width === b.width;

/**
 * Roll log as a tab that sits on the XP strip above the HUD's stat pills and opens upward, like the
 * Concentration tray. The log's entries are passed in as children. `anchorElements`, when given, returns the elements
 * to sit on instead (the DM's Tools and Award XP buttons): the tab spans them and opens upward from their top.
 */
export default function RollLogTab({ open, onOpenChange, count = 0, children, anchorElements = null }) {
  const id = useId();
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    let dock = null, status = null, watched = [];
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => update());
    const updateFromElements = () => {
      const elements = (anchorElements() || []).filter(Boolean);
      if (elements.length !== watched.length || elements.some((element, index) => element !== watched[index])) {
        resize?.disconnect();
        watched = elements;
        elements.forEach((element) => resize?.observe(element));
      }
      const rects = elements.map((element) => element.getBoundingClientRect()).filter((rect) => rect.width > 0);
      if (!rects.length) { setAnchor(null); return; }
      const left = Math.min(...rects.map((rect) => rect.left)), right = Math.max(...rects.map((rect) => rect.right));
      const next = { left: Math.max(8, Math.round(left)), bottom: Math.max(0, Math.round(window.innerHeight - Math.min(...rects.map((rect) => rect.top)))), width: Math.max(240, Math.round(right - left)) };
      setAnchor((previous) => (sameAnchor(previous, next) ? previous : next));
    };
    const update = () => {
      if (anchorElements) { updateFromElements(); return; }
      const nextDock = document.querySelector('.player-combat-hud');
      const nextStatus = nextDock?.querySelector('.combat-hud-dock__status') || null;
      if (nextDock !== dock || nextStatus !== status) {
        resize?.disconnect();
        dock = nextDock; status = nextStatus;
        if (dock) resize?.observe(dock);
        if (status) resize?.observe(status);
      }
      if (!dock) { setAnchor(null); return; }
      const statusRect = status?.getBoundingClientRect();
      const base = statusRect?.width > 0 ? statusRect : dock.getBoundingClientRect();
      const next = { left: Math.max(8, Math.round(base.left)), bottom: Math.max(0, Math.round(window.innerHeight - getHudDockTop(dock))), width: Math.max(240, Math.round(base.width)) };
      setAnchor((previous) => (sameAnchor(previous, next) ? previous : next));
    };
    update();
    // The HUD can mount after this tab (or switch to its mobile layout); watch for its panels.
    const mounts = new MutationObserver(update);
    mounts.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    return () => { resize?.disconnect(); mounts.disconnect(); window.removeEventListener('resize', update); };
  }, [anchorElements]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!anchor) return null;
  return createPortal(
    <section className="roll-log-tab" aria-label="Roll log" style={{ left: anchor.left, bottom: anchor.bottom, width: anchor.width }}>
      <button type="button" className="roll-log-tab__toggle" aria-expanded={open} aria-controls={id} onClick={() => onOpenChange(!open)}>
        <span>Log{count > 0 && <span className="roll-log-tab__count">{count}</span>}</span>
        <span aria-hidden="true">{open ? '▾' : '▴'}</span>
      </button>
      {open && (
        <div id={id} className="roll-log-tab__items">
          {count > 0 ? children : <p className="roll-log-tab__empty">No rolls yet this session.</p>}
        </div>
      )}
    </section>,
    document.body
  );
}
